import { useMutation } from "@apollo/client";
import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  Flex,
} from "@chakra-ui/react";
import {
  InternalAdminCreateTagMutation,
  InternalAdminCreateTagMutationVariables,
} from "gql/graphql";
import React, { memo, useCallback, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { HASH_KEY, SERVER_ERROR } from "modules/utils/Constants";
import {
  getMessageFromError,
  sortByName,
  stopPropagate,
} from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";
import useQueryString from "modules/hooks/use-query-string";
import InternalAdminCreateTagSelect, {
  INewTagOption,
} from "../components/internal-admin-create-tag-select";
import INTERNAL_ADMIN_CREATE_TAG_MUTATION from "../_networking/internal-admin-create-tag";
import useInternalAdminTagList from "../hooks/use-internal-admin-tag-list";
import useInternalAdminTagCategoryList from "../hooks/use-internal-admin-tag-category-list";

interface IProps {
  onSaveCallback?: Function;
}

const InternalAdminCreateTagModal = ({ onSaveCallback }: IProps) => {
  const { qsOrganizationId } = useQueryString();
  const { closeModal } = useOverlay();

  const { tags: parentTags } = useInternalAdminTagList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
      parentTags: true,
    },
  });

  const { tagCategories: messageTagCategories } =
    useInternalAdminTagCategoryList({
      organizationId: qsOrganizationId,
      input: {
        limit: 1000000,
        offset: 0,
      },
    });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    clearErrors,
  } = useForm({
    mode: "onSubmit",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [value, setValue] = useState<readonly INewTagOption[]>([]);

  const tagCategoryId = watch("tagCategoryId");
  const parentTagId = watch("parentTagId");

  const isTagNameDisabled = !tagCategoryId && !parentTagId;

  const [internalAdminCreateTagMutationAPI] = useMutation<
    InternalAdminCreateTagMutation,
    InternalAdminCreateTagMutationVariables
  >(INTERNAL_ADMIN_CREATE_TAG_MUTATION);

  const duplicateTags = useMemo(
    () => value.filter((v) => !!v.duplicateTag).map((v) => v.duplicateTag!),
    [value],
  );

  const hasDuplicate = duplicateTags.length > 0;

  const onSubmit = useCallback(
    async ({ parentTagId, tagCategoryId }: FieldValues) => {
      if (!qsOrganizationId) return;
      if (value.length === 0) {
        return;
      }

      setIsSubmitting(true);
      await Promise.all(
        value.map((name) =>
          internalAdminCreateTagMutationAPI({
            variables: {
              organizationId: qsOrganizationId,
              input: {
                name: name.value,
                parentTagId: parentTagId || null,
                tagCategoryId: tagCategoryId || null,
              },
            },
          }),
        ),
      );
      setIsSubmitting(false);
      if (onSaveCallback) {
        onSaveCallback();
      }
      closeModal();
    },
    [
      qsOrganizationId,
      value,
      onSaveCallback,
      closeModal,
      internalAdminCreateTagMutationAPI,
    ],
  );

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Text fontWeight={600} fontSize={24} textAlign="center">
            Create New Tags
          </Text>
        </ModalHeader>
        <ModalBody>
          <form
            id="create-tag-form"
            onSubmit={stopPropagate(handleSubmit(onSubmit))}
          >
            <Stack p={4} spacing={4}>
              <Stack
                spacing={4}
                bg={colors.whiteTertiary}
                p={3}
                borderRadius={8}
              >
                <FormControl isInvalid={!!errors?.tagCategoryId}>
                  <FormLabel fontSize="sm">Category </FormLabel>
                  <Select
                    isDisabled={!!parentTagId}
                    placeholder="Select Tag Category"
                    bg="white"
                    {...register("tagCategoryId", {
                      required: {
                        message:
                          "Either a Parent Tag or a Category is required",
                        value: !parentTagId,
                      },
                      deps: ["parentTagId"],
                    })}
                  >
                    {[...messageTagCategories]
                      .sort(sortByName)
                      .map((tagCategory) => (
                        <option key={tagCategory.id} value={tagCategory.id}>
                          {tagCategory.name}
                        </option>
                      ))}
                  </Select>
                  <FormErrorMessage>
                    {getMessageFromError(errors.tagCategoryId?.message)}
                  </FormErrorMessage>
                </FormControl>
                <Flex align="center">
                  <Divider borderColor={colors.gray30} />
                  <Text
                    px={3}
                    fontSize={14}
                    color={colors.grayMain}
                    fontWeight={500}
                  >
                    OR
                  </Text>
                  <Divider borderColor={colors.gray30} />
                </Flex>
                <FormControl isInvalid={!!errors?.parentTagId}>
                  <FormLabel>
                    <HStack>
                      <Text fontSize="sm">Parent Tag</Text>
                      <Text fontSize="xs" color={colors.gray30}>
                        (optional)
                      </Text>
                    </HStack>
                  </FormLabel>
                  <Select
                    placeholder="Select Parent Tag"
                    isDisabled={!!tagCategoryId}
                    bg="white"
                    {...register("parentTagId", {
                      required: {
                        message:
                          "Either a Parent Tag or a Category is required",
                        value: !tagCategoryId,
                      },
                      deps: ["tagCategoryId"],
                    })}
                  >
                    {parentTags.sort(sortByName).map((parentTag) => (
                      <option key={parentTag.id} value={parentTag.id}>
                        {HASH_KEY}
                        {parentTag.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {getMessageFromError(errors.parentTagId?.message)}
                  </FormErrorMessage>
                </FormControl>
              </Stack>
              <FormControl isInvalid={!!errors?.name}>
                <FormLabel fontSize="sm">Tag Name</FormLabel>
                <InternalAdminCreateTagSelect
                  parentTagId={parentTagId}
                  isDisabled={isTagNameDisabled}
                  onChange={setValue}
                />
                <FormErrorMessage>
                  {getMessageFromError(errors?.name?.message)}
                </FormErrorMessage>
              </FormControl>
              {hasDuplicate && (
                <Text color={colors.red}>Duplicate tags found</Text>
              )}
              {errors.SERVER_ERROR && (
                <Text color={colors.red}>
                  {getMessageFromError(errors?.SERVER_ERROR?.message)}
                </Text>
              )}{" "}
              <HStack w="full" justifyContent="center">
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  isDisabled={value.length === 0 || hasDuplicate}
                  type="submit"
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminCreateTagModal);
